import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Layout";
import AdminTransfers from "@/pages/AdminTransfers";
import AdminStats from "@/pages/AdminStats";
import AdminClubs from "@/pages/AdminClubs";
import AdminSettings from "@/pages/AdminSettings";

const TABS = [["rappels", "Rappels"], ["virements", "Virements"], ["clubs", "Clubs"], ["reglages", "Réglages"]];

const sortList = (l) => [...l].sort((a, b) => (a.done - b.done) || b.created_at.localeCompare(a.created_at));
const fmtWhen = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

function Row({ r, onChange }) {
  const [note, setNote] = useState(r.note || "");
  const patch = async (body) => {
    try { const { data } = await api.patch(`/admin/callbacks/${r.id}`, body); onChange(data); return true; }
    catch (e) { toast.error(apiError(e)); return false; }
  };
  return (
    <li className={`card p-5 ${r.done ? "opacity-70" : ""}`} data-testid={`callback-row-${r.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer select-none shrink-0 order-last sm:order-first">
          <input type="checkbox" className="w-5 h-5 accent-[#1E6B45]" checked={r.done} onChange={(e) => patch({ done: e.target.checked })} data-testid={`callback-done-${r.id}`} />
          Rappelé
        </label>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <p className="font-display text-lg font-bold">{r.name}</p>
            <p className="text-sm text-[#5B665F]">{r.club}</p>
            <span className={`pill ml-auto ${r.done ? "bg-[#DFF3E6] text-[#145A36]" : "bg-[#FDEBD3] text-[#8A4B00]"}`}>{r.done ? "Rappelé" : "À rappeler"}</span>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm">
            <a href={`tel:${encodeURIComponent(r.phone.replace(/[^\d+]/g, ""))}`} className="link inline-flex items-center gap-1.5" data-testid={`callback-phone-${r.id}`}><Phone size={14} />{r.phone}</a>
            <span className="text-[#5B665F]">Créneau : {r.slot || "non précisé"}</span>
            <span className="text-[#5B665F]" data-testid={`callback-date-${r.id}`}>Demande du {fmtWhen(r.created_at)}</span>
          </div>
          <textarea className="field h-auto min-h-[70px] py-2 mt-3" placeholder="Note (ex. rappeler jeudi, intéressé par l'offre…)" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`callback-note-${r.id}`} />
          {note !== (r.note || "") && (
            <button className="btn-primary btn-sm mt-2" onClick={async () => (await patch({ note })) && toast.success("Note enregistrée")} data-testid={`callback-note-save-${r.id}`}>Enregistrer la note</button>
          )}
        </div>
      </div>
    </li>
  );
}

const fmtEur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0).replace(/\u202f/g, "\u00a0");

function Reservations() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/admin/reservations").then((r) => setList(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  const paid = list ? list.filter((x) => x.payment_status === "paid").length : 0;
  return (
    <section className="mb-12" data-testid="admin-reservations">
      <h2 className="text-2xl font-bold tracking-tight">Réservations fondateur</h2>
      <p className="text-sm text-[#5B665F] mt-1" data-testid="admin-reservations-count">{list ? `${paid} place${paid > 1 ? "s" : ""} payée${paid > 1 ? "s" : ""}` : "Chargement…"}</p>
      <ul className="space-y-3 mt-5">
        {list?.map((r) => (
          <li key={r.session_id} className="card p-5 flex flex-wrap items-baseline gap-x-5 gap-y-1" data-testid={`reservation-row-${r.session_id}`}>
            <p className="font-display text-lg font-bold">{r.name}</p>
            <p className="text-sm text-[#5B665F]">{r.club}</p>
            <a href={`mailto:${encodeURIComponent(r.email)}`} className="text-sm link">{r.email}</a>
            <span className="text-sm text-[#5B665F]">{fmtEur(r.amount)} · payé le {fmtWhen(r.paid_at || r.created_at)}</span>
            <span className={`pill ml-auto ${r.payment_status === "paid" ? "bg-[#DFF3E6] text-[#145A36]" : "bg-[#EEF0EB] text-[#5B665F]"}`}>{r.payment_status === "paid" ? "Payé" : "Remboursé"}</span>
          </li>
        ))}
        {list?.length === 0 && <li className="card p-6 text-sm text-[#5B665F]" data-testid="admin-reservations-empty">Aucune réservation pour l'instant.</li>}
      </ul>
    </section>
  );
}

export default function AdminCallbacks() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/admin/callbacks").then((r) => setList(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  const onChange = (doc) => setList((l) => sortList(l.map((x) => (x.id === doc.id ? doc : x))));
  const todo = list ? list.filter((x) => !x.done).length : 0;
  const [tab, setTab] = useState("rappels");
  const tabCls = (k) => `px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === k ? "bg-[#13201A] text-white" : "text-[#5B665F] hover:text-[#13201A]"}`;

  return (
    <div className="min-h-screen" data-testid="admin-callbacks-page">
      <header className="bg-[#13201A]">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 h-16 flex items-center gap-4">
          <Logo />
          <span className="text-sm text-white/70 hidden sm:inline">Administration</span>
          <a href="/" className="ml-auto text-sm font-semibold text-[#3DDC84] hover:text-white" data-testid="admin-edit-landing-link">Modifier la page d'accueil</a>
          <button className="text-white/80 hover:text-white inline-flex items-center gap-2 text-sm font-semibold" onClick={async () => { await logout(); nav("/connexion"); }} data-testid="admin-logout"><LogOut size={16} /> Déconnexion</button>
        </div>
      </header>
      <main className="max-w-[1100px] mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <AdminStats refreshKey={tab} />
        <div className="flex max-w-full overflow-x-auto gap-1 p-1 rounded-xl bg-white border border-[#E1E4DD] mb-8 w-fit" role="tablist">
          {TABS.map(([k, label]) => <button key={k} role="tab" className={`${tabCls(k)} whitespace-nowrap`} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}>{label}</button>)}
        </div>
        {tab === "virements" ? <AdminTransfers /> : tab === "clubs" ? <AdminClubs /> : tab === "reglages" ? <AdminSettings /> : (
          <>
            <Reservations />
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Demandes de rappel</h1>
            <p className="text-sm text-[#5B665F] mt-2" data-testid="admin-callbacks-count">{list ? `${todo} à rappeler · ${list.length} au total` : "Chargement…"}</p>
            <ul className="space-y-3 mt-8">
              {list?.map((r) => <Row key={r.id} r={r} onChange={onChange} />)}
              {list?.length === 0 && <li className="card p-6 text-sm text-[#5B665F]" data-testid="admin-callbacks-empty">Aucune demande pour l'instant.</li>}
            </ul>
          </>
        )}
      </main>
    </div>
  );
}
